<template>
  <v-card class="basic-modal record-configuration-modal" elevation="4">
    <v-card-title class="d-flex justify-space-between">
      <h5 class="">
        <v-icon color="primary" size="25" class="mr-3 text--heading font-weight-bold"> $settings</v-icon>
        {{ $t('heading.hosting.emailAddress.modal.show.spfdkimConfig') }}
      </h5>
      <v-btn icon x-small @click="$emit('modal-close')">
        <v-icon size="24">$close</v-icon>
      </v-btn
      >
    </v-card-title>

    <v-card-text class="pb-8">
      <div class="record-box">
        <h5 class="record-box-title">{{ $t('heading.hosting.emailAddress.emailDomain.record.spf') }}
          <div
            v-if="!isSpfConfigured"
            class="record-label failed"
          >{{ $t('heading.hosting.emailAddress.emailDomain.record.failed') }}
          </div>
          <div
            v-else
            class="record-label configured"
          >{{ $t('heading.hosting.emailAddress.emailDomain.record.configured') }}
          </div>
        </h5>
        <p class="record-box-desc p-d">{{ $t('heading.hosting.emailAddress.modal.show.allowEmail') }} </p>
        <div
          v-if="spfRecords.length === 0"
          class="well well--gray py-3 px-4 mb-2"
        >
          {{ $t('message.emptyTable.noRecords') }}
        </div>
        <div
          v-else
          v-for="(record, index) in spfRecords"
          :key="index"
          class="well well--gray py-3 px-4 mb-2"
        >
          <ul class="record-box-list">
            <li
              class="record-box-list-item"
              v-for="(item, name) in record"
              :key="`${index}-${name}`"
              @click="copyElement($event)"
              @mouseover="hoveredIndex.spf = `${index}-${name}`"
              @mouseleave="hoveredIndex.spf = null, valueCopied = false"
            >
              <span>{{ $t(`general.${name}`) }}: </span>
              <span class="record-box-copy-element heading--text">
                <span>{{ item }}</span>
                <v-tooltip
                  open-delay="150"
                  top
                  content-class="copy-record-val-tooltip"
                  transition="custom-tooltip"
                  :value="hoveredIndex.spf === `${index}-${name}`"
                >
                  <template v-slot:activator="{ attrs }">
                    <v-icon
                      class="copy-icon"
                      :size="16"
                      v-bind="attrs"
                    >
                      $copy
                    </v-icon>
                  </template>
                  <div>
                    <p class="p-3 mb-0 record-box-copy-element-tooltip">
                      <span v-if="valueCopied" class="copied primary--text">{{ $t('tooltip.copied') }}</span>
                      <span v-else class="copy">{{ $t('tooltip.copy') }}</span>
                    </p>
                  </div>
                </v-tooltip>
              </span>
            </li>
          </ul>
        </div>
      </div>
      <div class="record-box mt-8">
        <h5 class="record-box-title">
          <span>{{ $t('heading.hosting.emailAddress.emailDomain.record.dkim') }}</span>
          <div
            v-if="!isDkimConfigured"
            class="record-label failed"
          >{{ $t('heading.hosting.emailAddress.emailDomain.record.failed') }}
          </div>
          <div
            v-else
            class="record-label configured"
          >{{ $t('heading.hosting.emailAddress.emailDomain.record.configured') }}
          </div>
        </h5>
        <p class="record-box-desc p-d">{{ $t('heading.hosting.emailAddress.modal.show.digitalSignature') }}</p>
        <div
          v-if="dkimRecords.length === 0"
          class="well well--gray py-3 px-4 mb-2"
        >
          {{ $t('message.emptyTable.noRecords') }}
        </div>
        <div
          v-else
          v-for="(record, index) in dkimRecords"
          :key="index"
          class="well well--gray py-3 px-4 mb-2"
        >
          <ul class="record-box-list">
            <li
              class="record-box-list-item"
              v-for="(item, name) in record"
              :key="`${index}-${name}`"
              @click="copyElement($event)"
              @mouseover="hoveredIndex.dkim = `${index}-${name}`"
              @mouseleave="hoveredIndex.dkim = null, valueCopied = false"
            >
              <span>{{ $t(`general.${name}`) }}: </span>
              <span class="record-box-copy-element heading--text">
                <span>{{ item }}</span>
                <v-tooltip
                  open-delay="150"
                  top
                  content-class="copy-record-val-tooltip"
                  transition="custom-tooltip"
                  :value="hoveredIndex.dkim === `${index}-${name}`"
                >
                  <template v-slot:activator="{ attrs }">
                    <v-icon
                      class="copy-icon"
                      :size="16"
                      v-bind="attrs"
                    >
                      $copy
                    </v-icon>
                  </template>
                  <div>
                    <p class="p-3 mb-0 record-box-copy-element-tooltip">
                      <span v-if="valueCopied" class="copied primary--text">{{ $t('tooltip.copied') }}</span>
                      <span v-else class="copy">{{ $t('tooltip.copy') }}</span>
                    </p>
                  </div>
                </v-tooltip>
              </span>
            </li>
          </ul>
        </div>
      </div>
    </v-card-text>
    <v-card-actions class="pt-0">
      <v-btn
        large
        elevation="0"
        color=""
        outlined
        class="w-100"
        @click="$emit('modal-close')"
      >
        {{ $t('button.cancel') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
export default {
  props: {
    domain: {
      type: [Object, null],
      required: true,
    }
  },
  data: function () {
    return {
      hoveredIndex: {
        spf: null,
        dkim: null
      },
      valueCopied: false,
    };
  },
  computed: {
    isDkimConfigured() {
      if (this.domain === null) {
        return false;
      }
      return this.domain.dkim_info.state;
    },
    dkimRecords() {
      if (this.domain === null) {
        return [];
      }
      return this.domain.dkim_info.records;
    },
    isSpfConfigured() {
      if (this.domain === null) {
        return false;
      }
      return this.domain.spf_info.state;
    },
    spfRecords() {
      if (this.domain === null) {
        return [];
      }
      return this.domain.spf_info.records;
    }
  },
  methods: {
    copyElement(event) {
      const el = event.currentTarget.querySelector(".record-box-copy-element");
      if (el) {
        const text = el.innerText || el.textContent;
        navigator.clipboard.writeText(text).then(() => {
          this.valueCopied = true;
        });
      }
    }
  },
};
</script>

<style lang="scss" scoped>
.v-dialog {
  .v-card {
    .v-card__actions {
      padding-top: 16px;
    }

    .v-card__title {
      min-height: 96px;
    }

    .v-card__text {
      -webkit-line-clamp: initial;
    }
  }

  .v-card::v-deep {
    .v-card__title {
      flex-wrap: nowrap;

      .v-btn {
        align-self: flex-start;
      }
    }
  }
}

.copy-record-val-tooltip {
  padding: 8px 16px;
  border-radius: 8px;

  &:after {
    top: unset;
    bottom: -8px;
  }
}

.record {
  &-label {
    margin-left: 12px;
    font-size: 12px;
    font-style: normal;
    font-weight: 700;
    padding: 3px 5px;
    border-radius: 5px;
    text-transform: capitalize;
    height: 24px;
    display: flex;
    align-items: center;

    &.failed {
      color: var(--v-error-base);
      background: rgba(231, 29, 29, 0.15);
    }

    &.configured {
      color: var(--v-primary-darken1);
      background: var(--v-primary-lighten2)
    }
  }

  &-box {
    &-title {
      display: flex;
      align-items: center;
      margin-bottom: 8px;
      color: var(--gray-pa-1100-headings, #21212B);
      font-size: 18px;
      line-height: 24px;
    }

    &-list {
      &-item {
        cursor: pointer;

        .copy-icon {
          transition: 0.3s;
          opacity: 0;
          visibility: hidden;
          margin-left: 4px;
          color: var(--v-primary-base);
        }

        &:hover {
          .copy-icon {
            opacity: 1;
            visibility: visible;
          }
        }
      }
    }

    &-copy-element-tooltip {
      width: 50px;
      text-align: center;
    }
  }
}
</style>